import React, { useState, useEffect } from "react";
import { AiOutlineDown, AiOutlineUp } from "react-icons/ai";
import MetadataDisplay from "./MetadataDisplay";
import MetadataEditor from "./MetadataEditor";
import ActionButtons from "./ActionButtons";
import Pagination from "./Pagination";
import LoadingOverlay from "./LoadingOverlay";
import EmptyState from "./EmptyState";

/**
 * DocumentGrid - Componente reutilizable para mostrar grillas de documentos
 * 
 * @param {Array} documents - Lista de documentos a mostrar
 * @param {Array} metadataSpecs - Especificaciones de metadata
 * @param {Boolean} loading - Estado de carga
 * @param {Array} columns - Definición de columnas
 * @param {Array} actions - Definición de acciones
 * @param {Boolean} showMetadata - Mostrar columna de metadata
 * @param {Boolean} metadataEditable - Permitir editar metadata
 * @param {Function} onMetadataEdit - Callback al editar metadata
 * @param {Object} permissions - Permisos del usuario
 * @param {Object} pagination - Configuración de paginación
 * @param {Function} onPaginationChange - Callback al cambiar paginación
 * @param {String} emptyMessage - Mensaje cuando no hay documentos
 * @param {Function} onRowClick - Callback al hacer click en una fila
 * @param {String} className - Clase CSS adicional
 * @param {Object} style - Estilos adicionales
 * @param {Boolean} selectable - Habilitar selección por checkboxes
 * @param {Array} selectedIds - IDs de documentos seleccionados
 * @param {Function} onSelectionChange - Callback al cambiar selección
 */
const DocumentGrid = ({
    documents = [],
    metadataSpecs = [],
    loading = false,
    columns = [],
    actions = [],
    showMetadata = true,
    metadataEditable = false,
    onMetadataEdit,
    permissions = {},
    pagination,
    onPaginationChange,
    emptyMessage = "No se encontraron documentos",
    onRowClick,
    className = "",
    style = {},
    sortConfig = [],
    onSortChange,
    selectable = false,
    selectedIds = [],
    onSelectionChange
}) => {
    const [expandedRows, setExpandedRows] = useState(new Set());

    // Reset de filas expandidas cuando cambia el set de documentos (típicamente
    // por cambio de serie en /control-de-calidad o /doctree, también en
    // paginación y filtros). Sin esto, si por coincidencia un docId de la
    // nueva lista coincide con uno expandido previo, queda metadata abierta
    // sobre un doc que el usuario no había desplegado. Reproduce el bug
    // reportado en QC al cambiar entre series.
    useEffect(() => {
        setExpandedRows(new Set());
    }, [documents]);

    // Handle selection
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const allIds = documents.map(doc => doc.idDoc);
            onSelectionChange?.(allIds);
        } else {
            onSelectionChange?.([]);
        }
    };

    const handleSelectOne = (docId, checked) => {
        if (checked) {
            onSelectionChange?.([...selectedIds, docId]);
        } else {
            onSelectionChange?.(selectedIds.filter(id => id !== docId));
        }
    };

    const isAllSelected = documents.length > 0 && selectedIds.length === documents.length;
    const isIndeterminate = selectedIds.length > 0 && selectedIds.length < documents.length;

    // Toggle expansión de metadata
    const toggleRow = (docId) => {
        setExpandedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(docId)) {
                newSet.delete(docId);
            } else {
                newSet.add(docId);
            }
            return newSet;
        });
    };

    // Manejar click en header para ordenar
    const handleHeaderClick = (columnKey, sortable) => {
        if (!sortable || !onSortChange) return;

        const existingSort = sortConfig.find(s => s.field === columnKey);
        let newSortConfig;

        if (!existingSort) {
            // Primer click: ASC, y reemplaza otros ordenamientos (Single Sort Priority para headers)
            // Se puede debatir si acumular o reemplazar. Comúnmente en grids simples es reemplazar.
            // Para mantener consistencia con SortControl que añade al principio, aquí reemplazamos para "enfocar" en esta columna.
            newSortConfig = [{ field: columnKey, order: 'asc' }];
        } else if (existingSort.order === 'asc') {
            // Segundo click: DESC
            newSortConfig = [{ field: columnKey, order: 'desc' }];
        } else {
            // Tercer click: Remover orden
            newSortConfig = [];
        }

        onSortChange(newSortConfig);
    };

    // Obtener icono de ordenamiento
    const getSortIcon = (columnKey) => {
        const sort = sortConfig.find(s => s.field === columnKey);
        if (!sort) return null;

        return (
            <span style={{ marginLeft: '5px', display: 'inline-block' }}>
                {sort.order === 'asc' ? '▲' : '▼'}
            </span>
        );
    };

    // Guardar metadata editada (cierra la fila al guardar con éxito)
    const handleMetadataSave = async (docId, newMetadata) => {
        if (onMetadataEdit) {
            await onMetadataEdit(docId, newMetadata);
            setExpandedRows(prev => {
                const next = new Set(prev);
                next.delete(docId);
                return next;
            });
        }
    };

    // Descartar cambios: colapsa la fila y descarta el buffer interno del editor
    const handleMetadataCancel = (docId) => {
        setExpandedRows(prev => {
            const next = new Set(prev);
            next.delete(docId);
            return next;
        });
    };

    // Filtrar acciones visibles según permisos e inyectar acción de metadata
    const getVisibleActions = (doc) => {
        let visibleActions = actions.filter(action => {
            if (!action.show) return true;
            return action.show(doc, permissions);
        });

        // Inyectar acción de metadata al inicio si showMetadata es true
        if (showMetadata) {
            const isExpanded = expandedRows.has(doc.idDoc);
            const metadataAction = {
                key: '_metadata_toggle',
                icon: isExpanded ? <AiOutlineUp /> : <AiOutlineDown />,
                label: isExpanded ? 'Ocultar metadata' : 'Mostrar metadata',
                onClick: (d) => toggleRow(d.idDoc),
                color: '#00468B',
                _isMetadataToggle: true
            };
            visibleActions = [metadataAction, ...visibleActions];
        }

        return visibleActions;
    };

    // Calcular colspan para fila de metadata
    const getMetadataColspan = () => {
        let colspan = columns.length;
        if (selectable) colspan += 1; // +1 for checkbox column
        if (actions.length > 0) colspan += 1;
        return colspan;
    };

    return (
        <div className={`document-grid ${className}`} style={style}>
            {/* Loading Overlay */}
            {loading && <LoadingOverlay />}

            {/* Tabla */}
            {documents.length > 0 ? (
                <div style={{ position: 'relative' }}>
                    <div style={{
                        opacity: loading ? 0.3 : 1,
                        transition: 'opacity 0.3s ease-in-out'
                    }}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    {selectable && (
                                        <th style={{ ...styles.th, width: '50px', textAlign: 'center' }}>
                                            <input
                                                type="checkbox"
                                                checked={isAllSelected}
                                                ref={input => {
                                                    if (input) {
                                                        input.indeterminate = isIndeterminate;
                                                    }
                                                }}
                                                onChange={handleSelectAll}
                                                style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                                                title={isAllSelected ? "Deseleccionar todos" : "Seleccionar todos"}
                                            />
                                        </th>
                                    )}
                                    {columns.map(col => (
                                        <th
                                            key={col.key}
                                            onClick={() => handleHeaderClick(col.key, col.sortable)}
                                            style={{
                                                ...styles.th,
                                                width: col.width,
                                                textAlign: col.align || 'left',
                                                cursor: col.sortable ? 'pointer' : 'default',
                                                userSelect: 'none'
                                            }}
                                            title={col.sortable ? "Click para ordenar" : ""}
                                        >
                                            {col.label}
                                            {getSortIcon(col.key)}
                                        </th>
                                    ))}
                                    {actions.length > 0 && (
                                        <th style={{ ...styles.th, width: '200px', textAlign: 'center' }}>
                                            Acciones
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {documents.map((doc) => {
                                    const visibleActions = getVisibleActions(doc);
                                    const isExpanded = expandedRows.has(doc.idDoc);
                                    const canEditHere = metadataEditable && !!permissions.canEditMetadata;

                                    return (
                                        <React.Fragment key={doc.idDoc}>
                                            {/* Fila principal */}
                                            <tr
                                                style={{
                                                    ...styles.trHover,
                                                    backgroundColor: selectedIds.includes(doc.idDoc) ? '#E3F2FD' : 'transparent'
                                                }}
                                                onClick={() => onRowClick?.(doc)}
                                            >
                                                {selectable && (
                                                    <td style={{ ...styles.td, textAlign: 'center' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedIds.includes(doc.idDoc)}
                                                            onChange={(e) => {
                                                                e.stopPropagation();
                                                                handleSelectOne(doc.idDoc, e.target.checked);
                                                            }}
                                                            onClick={(e) => e.stopPropagation()}
                                                            style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                                                        />
                                                    </td>
                                                )}
                                                {columns.map(col => (
                                                    <td
                                                        key={col.key}
                                                        style={{
                                                            ...styles.td,
                                                            textAlign: col.align || 'left',
                                                            ...col.style
                                                        }}
                                                    >
                                                        {col.render ? col.render(doc) : doc[col.key]}
                                                    </td>
                                                ))}

                                                {actions.length > 0 && (
                                                    <td style={{ ...styles.td, textAlign: 'center' }}>
                                                        <ActionButtons
                                                            actions={visibleActions}
                                                            document={doc}
                                                        />
                                                    </td>
                                                )}
                                            </tr>

                                            {/* Fila expandible de metadata — edición directa en Revisión, view en Aprobación */}
                                            {showMetadata && isExpanded && (
                                                <tr>
                                                    <td colSpan={getMetadataColspan()} style={styles.metadataCell}>
                                                        {canEditHere ? (
                                                            <MetadataEditor
                                                                key={`editor-${doc.idDoc}`}
                                                                metadata={doc.metadata}
                                                                metadataSpec={metadataSpecs}
                                                                onSave={(newMetadata) => handleMetadataSave(doc.idDoc, newMetadata)}
                                                                onCancel={() => handleMetadataCancel(doc.idDoc)}
                                                            />
                                                        ) : (
                                                            <MetadataDisplay
                                                                metadata={doc.metadata}
                                                                metadataSpec={metadataSpecs}
                                                            />
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                !loading && <EmptyState message={emptyMessage} />
            )}

            {/* Paginación */}
            {pagination && onPaginationChange && documents.length > 0 && (
                <Pagination
                    value={pagination}
                    onChange={onPaginationChange}
                    disabled={loading}
                />
            )}
        </div>
    );
};

const styles = {
    table: {
        width: "100%",
        borderCollapse: "collapse",
        backgroundColor: "#fff",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    },
    th: {
        backgroundColor: "#00468B",
        color: "#fff",
        padding: "12px",
        fontWeight: "bold",
        fontSize: "14px",
        borderBottom: "2px solid #003366",
    },
    td: {
        padding: "12px",
        borderBottom: "1px solid #ddd",
        fontSize: "14px",
    },
    trHover: {
        cursor: "pointer",
        transition: "background-color 0.2s",
        "&:hover": {
            backgroundColor: "#f5f5f5",
        },
    },
    metadataButton: {
        backgroundColor: "transparent",
        border: "1px solid #00468B",
        borderRadius: "4px",
        padding: "6px 10px",
        cursor: "pointer",
        color: "#00468B",
        fontSize: "16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        transition: "all 0.2s",
    },
    metadataCell: {
        padding: "0",
        backgroundColor: "#F8F9FA",
    },
};

export default DocumentGrid;
